import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import { ArrowLeft, RotateCcw, Save, X } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useListCarrierAddOnServices,
  useUpdateCarrierAddOnService,
  type UpdateCarrierAddOnServiceInput,
} from "@/api/carrier-add-on-services";
import { useGetCarriers } from "@/api/carriers";
import type { CarrierAddOnService } from "@/api.types.v2";
import { PickupRatesEditor } from "./pickup-rates-editor";
import { PickupSlotsTab } from "./pickup-slots-tab";
import { ServiceEnablementPanel } from "./service-enablement-panel";

/**
 * Tabbed detail view for a single pickup service. Composes the three reusable
 * panels (rates, slot links, enablement) plus an inline Overview form that
 * ports the body of the old EditPickupServiceDialog. TimeSlots are fetched
 * once at this level and forwarded into PickupSlotLinks so the slot list isn't
 * re-fetched per tab mount.
 */
export function PickupServiceDetailScreen() {
  const { id } = useParams<{ id: string }>();

  const { data: services, isLoading: servicesLoading } =
    useListCarrierAddOnServices({ type: "PICKUP" });
  const { data: carriersResp } = useGetCarriers();

  const service = id ? services?.find((s) => s.id === id) : undefined;
  // Never render the raw provider UUID — fall back to an em-dash when the
  // carrier name can't be resolved (carrier list still loading, deleted, etc.).
  const providerName =
    carriersResp?.data.find((c) => c.id === service?.providerCarrierId)?.name ??
    "—";

  if (servicesLoading) {
    return (
      <div className="p-4 space-y-4">
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="p-4 space-y-4">
        <div className="text-center p-8 text-muted-foreground">
          <p>Pickup service not found.</p>
          <Button asChild variant="link" size="sm" className="mt-2">
            <Link to="/add-on-services">
              <ArrowLeft className="h-3 w-3 mr-1" />
              Back to Pickup Services
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <DetailHeader service={service} providerName={providerName} />

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="rates">Rates</TabsTrigger>
          <TabsTrigger value="slots">Time Slots</TabsTrigger>
          <TabsTrigger value="enablement">Enablement</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab service={service} />
        </TabsContent>
        <TabsContent value="rates">
          <PickupRatesEditor lockedServiceId={service.id} />
        </TabsContent>
        <TabsContent value="slots">
          <PickupSlotsTab serviceId={service.id} />
        </TabsContent>
        <TabsContent value="enablement">
          <ServiceEnablementPanel serviceId={service.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function DetailHeader({
  service,
  providerName,
}: {
  service: CarrierAddOnService;
  providerName: string;
}) {
  return (
    <div className="space-y-2">
      <div className="text-sm">
        <Link
          to="/add-on-services"
          className="text-muted-foreground hover:text-foreground inline-flex items-center"
        >
          <ArrowLeft className="h-3 w-3 mr-1" />
          Pickup Services
        </Link>
      </div>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-3xl font-bold">{service.name}</h1>
          <code className="font-mono text-sm text-muted-foreground">
            {service.code}
          </code>
          {service.active ? (
            <Badge
              variant="secondary"
              className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100"
            >
              Active
            </Badge>
          ) : (
            <Badge
              variant="secondary"
              className="bg-zinc-100 text-zinc-700 hover:bg-zinc-100"
            >
              Inactive
            </Badge>
          )}
        </div>
        <div className="text-sm text-muted-foreground whitespace-nowrap">
          Provider: <span className="text-foreground">{providerName}</span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overview tab — inline port of the old EditPickupServiceDialog body. Only
// changed fields go into the patch; Save is disabled when nothing changed.
// Resets local state on success so the form mirrors server truth.
// ---------------------------------------------------------------------------
function OverviewTab({ service }: { service: CarrierAddOnService }) {
  const initialCm = service.pickupDetails?.dimFactorCm ?? null;
  const initialInch = service.pickupDetails?.dimFactorInch ?? null;

  const [name, setName] = useState(service.name);
  const [active, setActive] = useState(service.active);
  const [dimFactorCm, setDimFactorCm] = useState(
    initialCm != null ? String(initialCm) : ""
  );
  const [dimFactorInch, setDimFactorInch] = useState(
    initialInch != null ? String(initialInch) : ""
  );

  // Re-seed local state ONLY when the user navigates to a different service
  // (new :id → new service.id). Background refetches of the SAME service must
  // not overwrite in-flight edits. Trade-off: server-side mutations by other
  // actors won't reflect until the user navigates away and back — acceptable;
  // admin screens are single-actor in practice.
  // The ref carries the latest `service` so the effect reads current values
  // without adding `service` to the dep list (which would defeat the point).
  const serviceRef = useRef(service);
  serviceRef.current = service;
  useEffect(() => {
    const s = serviceRef.current;
    setName(s.name);
    setActive(s.active);
    const cm = s.pickupDetails?.dimFactorCm ?? null;
    const inch = s.pickupDetails?.dimFactorInch ?? null;
    setDimFactorCm(cm != null ? String(cm) : "");
    setDimFactorInch(inch != null ? String(inch) : "");
  }, [service.id]);

  const update = useUpdateCarrierAddOnService();

  // Build the partial patch. Returns null if nothing changed. Returns
  // `undefined` if a numeric input is invalid (parsed but non-positive).
  const buildPatch = (): UpdateCarrierAddOnServiceInput | null | undefined => {
    const patch: UpdateCarrierAddOnServiceInput = {};
    const trimmedName = name.trim();
    if (trimmedName && trimmedName !== service.name) patch.name = trimmedName;
    if (active !== service.active) patch.active = active;

    const parseDim = (s: string): number | null | undefined => {
      const trimmed = s.trim();
      if (trimmed === "") return null;
      const n = Number(trimmed);
      if (!Number.isFinite(n) || n <= 0) return undefined;
      return n;
    };

    const cm = parseDim(dimFactorCm);
    if (cm === undefined) {
      toast.error("DIM (cm³) must be a positive number or blank.");
      return undefined;
    }
    // Serialized Decimals come back as strings — compare numerically so
    // "5000" vs 5000 doesn't show a phantom change.
    const initCm = initialCm != null ? Number(initialCm) : null;
    if (cm !== initCm) patch.dimFactorCm = cm;

    const inch = parseDim(dimFactorInch);
    if (inch === undefined) {
      toast.error("DIM (in³) must be a positive number or blank.");
      return undefined;
    }
    const initInch = initialInch != null ? Number(initialInch) : null;
    if (inch !== initInch) patch.dimFactorInch = inch;

    return Object.keys(patch).length === 0 ? null : patch;
  };

  // Cheap dirty-check for the Save-disabled state. Same shape as buildPatch
  // minus the toast. Crucially, invalid numeric input (e.g. user typed "abc"
  // into a DIM field) counts as dirty-but-invalid so Save STAYS ENABLED — the
  // Save handler then surfaces a toast. Silent disabling gives the user no
  // feedback about what's wrong.
  const hasChanges = (() => {
    if (name.trim() && name.trim() !== service.name) return true;
    if (active !== service.active) return true;
    const cmStr = dimFactorCm.trim();
    const initCm = initialCm != null ? Number(initialCm) : null;
    if (cmStr === "") {
      if (initCm !== null) return true;
    } else {
      const cmNum = Number(cmStr);
      // Garbage input → dirty-but-invalid → Save enabled → toast on click.
      if (!Number.isFinite(cmNum) || cmNum <= 0) return true;
      if (cmNum !== initCm) return true;
    }
    const inchStr = dimFactorInch.trim();
    const initInch = initialInch != null ? Number(initialInch) : null;
    if (inchStr === "") {
      if (initInch !== null) return true;
    } else {
      const inchNum = Number(inchStr);
      if (!Number.isFinite(inchNum) || inchNum <= 0) return true;
      if (inchNum !== initInch) return true;
    }
    return false;
  })();

  const handleSave = () => {
    const patch = buildPatch();
    if (patch === undefined) return; // validation toast already fired
    if (patch === null) {
      toast.message("Nothing to update.");
      return;
    }
    update.mutate(
      { id: service.id, data: patch },
      {
        onSuccess: (updated) => {
          toast.success("Pickup service updated.");
          // Reflect server truth — initialCm/Inch are read from `service`
          // (parent prop) which re-renders once the list query refetches.
          setName(updated.name);
          setActive(updated.active);
          const newCm = updated.pickupDetails?.dimFactorCm ?? null;
          const newInch = updated.pickupDetails?.dimFactorInch ?? null;
          setDimFactorCm(newCm != null ? String(newCm) : "");
          setDimFactorInch(newInch != null ? String(newInch) : "");
        },
        onError: (err: unknown) => {
          const e = err as { response?: { data?: { error?: string } } };
          toast.error(e.response?.data?.error ?? "Failed to update service.");
        },
      }
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Overview</CardTitle>
        <p className="text-sm text-muted-foreground">
          Code <code className="font-mono">{service.code}</code> and provider
          carrier can&apos;t be changed. To retire, toggle off the Active
          switch.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1">
          <Label htmlFor="ps-detail-code">Code</Label>
          <Input
            id="ps-detail-code"
            value={service.code}
            readOnly
            disabled
            className="font-mono"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="ps-detail-name">Name</Label>
          <Input
            id="ps-detail-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor="ps-detail-dim-cm">DIM factor (cm³)</Label>
            <Input
              id="ps-detail-dim-cm"
              type="number"
              step="any"
              value={dimFactorCm}
              onChange={(e) => setDimFactorCm(e.target.value)}
              placeholder="—"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ps-detail-dim-in">DIM factor (in³)</Label>
            <Input
              id="ps-detail-dim-in"
              type="number"
              step="any"
              value={dimFactorInch}
              onChange={(e) => setDimFactorInch(e.target.value)}
              placeholder="—"
            />
          </div>
        </div>
        <div className="flex items-center justify-between rounded-md border p-3">
          <div>
            <Label className="text-sm font-medium">Active</Label>
            <p className="text-xs text-muted-foreground">
              Inactive services are hidden from pickup booking flows but
              preserved for existing references.
            </p>
          </div>
          <Button
            variant={active ? "outline" : "default"}
            size="sm"
            onClick={() => setActive(!active)}
          >
            {active ? (
              <>
                <X className="h-3 w-3 mr-1" />
                Deactivate
              </>
            ) : (
              <>
                <RotateCcw className="h-3 w-3 mr-1" />
                Reactivate
              </>
            )}
          </Button>
        </div>
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={update.isPending || !hasChanges}
          >
            <Save className="h-4 w-4 mr-1" />
            {update.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
